import config from '../config';

const API_BASE_URL = import.meta.env.VITE_ENV_HOST_URL || config.urls.host;
const ASSET_API_BASE_URL = (import.meta.env.VITE_EMPLOYEE_CONNECT_BASE_URL || '').replace(/\/$/, '');
export const ASSET_TOKEN_KEY = 'assetToken';

export interface KnowledgeHubConfig {
  companyErpCode: string[];
  moduleErpCode: string[];
  applicationErpCode: string[];
}

type KnowledgeHubConfigByClient = Record<string, Partial<KnowledgeHubConfig>>;

interface KnowledgeHubUserScope {
  clientCode?: string;
  isAdmin: boolean;
}

export interface KnowledgeHubFile {
  id: string;
  name?: string;
  description?: string;
  referenceNumber?: string;
  fileName: string;
  fileType?: string;
  fileSize?: number;
  createdDate?: string;
  downloadAllowed?: boolean;
  presignedUrl?: string;
  tags?: Array<string | { name?: string }>;
  company?: { id?: string; name?: string; erpCode?: string };
  application?: { id?: string; name?: string; erpCode?: string };
  module?: { id?: string; name?: string; erpCode?: string };
}

interface GroupedFiles {
  moduleId: string;
  moduleName?: string;
  files: KnowledgeHubFile[];
}

const parseResponse = async <T,>(response: Response): Promise<T> => {
  if (!response.ok) {
    const error = await response.json().catch(() => null);
    throw new Error(error?.message || `Request failed (${response.status})`);
  }
  return response.json();
};

const getAssetApiBaseUrl = () => {
  if (!ASSET_API_BASE_URL) {
    throw new Error('VITE_EMPLOYEE_CONNECT_BASE_URL is not configured.');
  }
  return ASSET_API_BASE_URL;
};

export const saveAssetToken = (token?: string | null) => {
  if (token) localStorage.setItem(ASSET_TOKEN_KEY, token);
};

export const getKnowledgeHubFiles = async (
  search?: string,
  userScope?: KnowledgeHubUserScope,
  onProgress?: (progress: number) => void
) => {
  const configResponse = await fetch(`${API_BASE_URL}/master/knowledge-hub`, {
    credentials: 'include',
  });
  const configPayload = await parseResponse<{
    data?: { configValue?: KnowledgeHubConfigByClient };
  }>(configResponse);
  const configByClient = configPayload.data?.configValue || {};
  const configEntries = Object.entries(configByClient);

  const selectedConfigs = userScope?.isAdmin
    ? configEntries.map(([, clientConfig]) => clientConfig)
    : configEntries
        .filter(([clientCode]) =>
          clientCode.toUpperCase() === userScope?.clientCode?.trim().toUpperCase()
        )
        .map(([, clientConfig]) => clientConfig);

  if (!selectedConfigs.length) {
    throw new Error(
      userScope?.isAdmin
        ? 'Knowledge Hub configuration is unavailable.'
        : `Knowledge Hub configuration is unavailable for client ${userScope?.clientCode || 'unknown'}.`,
    );
  }

  const hubConfig = selectedConfigs.reduce<KnowledgeHubConfig>(
    (mergedConfig, clientConfig) => {
      (['companyErpCode', 'moduleErpCode', 'applicationErpCode'] as const).forEach(key => {
        const values = clientConfig[key];
        if (Array.isArray(values)) {
          mergedConfig[key] = Array.from(new Set([
            ...mergedConfig[key],
            ...values.filter((value): value is string => typeof value === 'string' && Boolean(value.trim())),
          ]));
        }
      });
      return mergedConfig;
    },
    { companyErpCode: [], moduleErpCode: [], applicationErpCode: [] },
  );
  const params = new URLSearchParams();

  (['companyErpCode', 'moduleErpCode', 'applicationErpCode'] as const).forEach(key => {
    const values = hubConfig[key];
    if (Array.isArray(values) && values.length) params.set(key, values.join(','));
  });
  if (search?.trim()) params.set('search', search.trim());

  const token = localStorage.getItem(ASSET_TOKEN_KEY);
  if (!token) throw new Error('Asset authorization token is unavailable. Please sign in again.');

  const filesResponse = await fetch(
    `${getAssetApiBaseUrl()}/asset-inventory/microservice/files?${params.toString()}`,
    { headers: { Authorization: `Bearer ${token}` } },
  );
  
  if (!filesResponse.ok) {
    const error = await filesResponse.json().catch(() => null);
    throw new Error(error?.message || `Request failed (${filesResponse.status})`);
  }

  let data;
  if (onProgress && filesResponse.body) {
    const contentLength = filesResponse.headers.get('content-length');
    const total = contentLength ? parseInt(contentLength, 10) : 0;
    let loaded = 0;
    let fakeProgress = 0;
    const reader = filesResponse.body.getReader();
    const chunks = [];
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      chunks.push(value);
      loaded += value.length;
      if (total) {
        onProgress(Math.round((loaded / total) * 100));
      } else {
        fakeProgress = fakeProgress + (90 - fakeProgress) * 0.15;
        onProgress(Math.round(fakeProgress));
      }
    }
    const allChunks = new Uint8Array(loaded);
    let position = 0;
    for (const chunk of chunks) {
      allChunks.set(chunk, position);
      position += chunk.length;
    }
    const text = new TextDecoder("utf-8").decode(allChunks);
    data = JSON.parse(text);
  } else {
    data = await filesResponse.json();
  }
  
  return (data.data || []).flatMap((group: GroupedFiles) => group.files || []);
};

export const getKnowledgeHubFileView = async (id: string, onProgress?: (progress: number) => void) => {
  const token = localStorage.getItem(ASSET_TOKEN_KEY);
  if (!token) throw new Error('Asset authorization token is unavailable. Please sign in again.');
  const response = await fetch(
    `${getAssetApiBaseUrl()}/asset-inventory/microservice/files/${encodeURIComponent(id)}/view`,
    { headers: { Authorization: `Bearer ${token}` } },
  );
  if (!response.ok) throw new Error(`Unable to open this file (${response.status})`);
  
  if (onProgress && response.body) {
    const contentLength = response.headers.get('content-length');
    const total = contentLength ? parseInt(contentLength, 10) : 0;
    let loaded = 0;
    let fakeProgress = 0;
    const reader = response.body.getReader();
    const stream = new ReadableStream({
      async start(controller) {
        while (true) {
          const { done, value } = await reader.read();
          if (done) {
            onProgress(100);
            break;
          }
          loaded += value.length;
          if (total) {
            onProgress(Math.round((loaded / total) * 100));
          } else {
            fakeProgress = fakeProgress + (99 - fakeProgress) * 0.1;
            onProgress(Math.round(fakeProgress));
          }
          controller.enqueue(value);
        }
        controller.close();
        reader.releaseLock();
      }
    });
    const contentType = response.headers.get('content-type') || '';
    return new Response(stream, { headers: { 'Content-Type': contentType } }).blob();
  }
  return response.blob();
};

export const getKnowledgeHubFilePublicUrl = (id: string) => {
  const token = localStorage.getItem(ASSET_TOKEN_KEY) || '';
  return `${getAssetApiBaseUrl()}/asset-inventory/microservice/files/${encodeURIComponent(id)}/view?token=${encodeURIComponent(token)}`;
};
